import type { AppUser } from "@/types/domain";

export const APP_PERMISSIONS = [
  "dashboard.read",
  "patients.read",
  "patients.write",
  "appointments.read",
  "appointments.write",
  "appointment_statuses.manage",
  "medical_records.read",
  "medical_records.write",
  "medical_records.edit_others",
  "medical_records.correct_finalized",
  "leads.read",
  "leads.write",
  "marketing.read",
  "marketing.write",
  "goals.read",
  "goals.write",
  "attendance.read",
  "attendance.write",
  "clinical_production.read",
  "clinical_production.generate",
  "clinical_production.cancel",
  "clinical_production.adjust",
  "clinical_production.audit",
  "clinical_production.export",
  "payouts.read",
  "payouts.rules.manage",
  "payouts.approve",
  "payouts.pay",
  "payouts.cancel",
  "payouts.adjust",
  "payouts.audit",
  "payouts.export",
  "management_overview.read",
  "management_overview.export",
  "management_overview.financial",
  "management_overview.production",
  "management_overview.payouts",
  "managerial_accrual.read",
  "managerial_accrual.data_quality",
  "managerial_accrual.manage_competence",
  "managerial_accrual.backfill",
  "managerial_income_statement.read",
  "managerial_income_statement.drilldown",
  "managerial_income_statement.export",
  "managerial_income_statement.data_quality",
  "expense_allocations.read",
  "expense_allocations.simulate",
  "expense_allocations.apply",
  "expense_allocations.reverse",
  "expense_allocations.export",
  "expense_allocations.rules.manage",
  "expense_allocations.settings.manage",
  "expense_allocations.audit",
  "expenses.read",
  "expenses.create",
  "expenses.update_draft",
  "expenses.cancel",
  "expenses.pay",
  "expenses.refund",
  "expenses.recurring.manage",
  "expenses.categories.manage",
  "expenses.audit",
  "expenses.export",
  "cash_result.read",
  "finance.read",
  "finance.write",
  "finance.sales.read",
  "finance.sales.create",
  "finance.sales.cancel",
  "finance.discounts.apply",
  "finance.surcharges.apply",
  "finance.receivables.create",
  "finance.payments.create",
  "finance.payments.refund",
  "finance.manual_charge.create",
  "finance.manual_cash.create",
  "finance.reconciliation.read",
  "finance.reconciliation.resolve",
  "professionals.read",
  "professionals.write",
  "users.read",
] as const;

export type AppPermission = (typeof APP_PERMISSIONS)[number];

export interface PermissionAction {
  id: AppPermission;
  label: string;
}

export interface PermissionGroup {
  key: string;
  label: string;
  actions: PermissionAction[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  { key: "dashboard", label: "Dashboard", actions: [{ id: "dashboard.read", label: "Visualizar" }] },
  {
    key: "patients",
    label: "Pacientes",
    actions: [
      { id: "patients.read", label: "Visualizar" },
      { id: "patients.write", label: "Criar / Editar" },
    ],
  },
  {
    key: "appointments",
    label: "Agenda",
    actions: [
      { id: "appointments.read", label: "Visualizar" },
      { id: "appointments.write", label: "Criar / Editar" },
      { id: "appointment_statuses.manage", label: "Gerenciar status" },
    ],
  },
  {
    key: "medical_records",
    label: "Prontuário",
    actions: [
      { id: "medical_records.read", label: "Visualizar" },
      { id: "medical_records.write", label: "Criar / Editar" },
      { id: "medical_records.edit_others", label: "Editar registros de outros profissionais" },
      { id: "medical_records.correct_finalized", label: "Adicionar correções a registros finalizados" },
    ],
  },
  {
    key: "leads",
    label: "Leads",
    actions: [
      { id: "leads.read", label: "Visualizar" },
      { id: "leads.write", label: "Criar / Editar" },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    actions: [
      { id: "marketing.read", label: "Visualizar" },
      { id: "marketing.write", label: "Criar / Editar" },
    ],
  },
  {
    key: "goals",
    label: "Metas",
    actions: [
      { id: "goals.read", label: "Visualizar" },
      { id: "goals.write", label: "Criar / Editar" },
    ],
  },
  {
    key: "attendance",
    label: "Atendimento",
    actions: [
      { id: "attendance.read", label: "Visualizar" },
      { id: "attendance.write", label: "Criar / Editar" },
    ],
  },
  {
    key: "clinical_production",
    label: "Produção clínica",
    actions: [
      { id: "clinical_production.read", label: "Visualizar" },
      { id: "clinical_production.generate", label: "Gerar por conclusão" },
      { id: "clinical_production.cancel", label: "Cancelar produção" },
      { id: "clinical_production.adjust", label: "Ajustar / backfill" },
      { id: "clinical_production.audit", label: "Consultar auditoria" },
      { id: "clinical_production.export", label: "Exportar" },
    ],
  },
  {
    key: "payouts",
    label: "Repasses profissionais",
    actions: [
      { id: "payouts.read", label: "Visualizar" },
      { id: "payouts.rules.manage", label: "Gerenciar regras" },
      { id: "payouts.approve", label: "Aprovar" },
      { id: "payouts.pay", label: "Pagar" },
      { id: "payouts.cancel", label: "Cancelar" },
      { id: "payouts.adjust", label: "Ajustar" },
      { id: "payouts.audit", label: "Auditar" },
      { id: "payouts.export", label: "Exportar" },
    ],
  },
  {
    key: "management_overview",
    label: "Visão gerencial",
    actions: [
      { id: "management_overview.read", label: "Visualizar" },
      { id: "management_overview.export", label: "Exportar" },
      { id: "management_overview.financial", label: "Blocos financeiros" },
      { id: "management_overview.production", label: "Blocos de produção" },
      { id: "management_overview.payouts", label: "Blocos de repasses" },
    ],
  },
  {
    key: "managerial_accrual",
    label: "Competência gerencial",
    actions: [
      { id: "managerial_accrual.read", label: "Visualizar" },
      { id: "managerial_accrual.data_quality", label: "Qualidade dos dados" },
      { id: "managerial_accrual.manage_competence", label: "Gerenciar competência" },
      { id: "managerial_accrual.backfill", label: "Executar backfill" },
    ],
  },
  {
    key: "managerial_income_statement",
    label: "DRE Gerencial",
    actions: [
      { id: "managerial_income_statement.read", label: "Visualizar" },
      { id: "managerial_income_statement.drilldown", label: "Detalhar linhas" },
      { id: "managerial_income_statement.export", label: "Exportar" },
      { id: "managerial_income_statement.data_quality", label: "Qualidade dos dados" },
    ],
  },
  {
    key: "expense_allocations",
    label: "Rateio gerencial",
    actions: [
      { id: "expense_allocations.read", label: "Visualizar" },
      { id: "expense_allocations.simulate", label: "Simular rateio" },
      { id: "expense_allocations.apply", label: "Aplicar rateio" },
      { id: "expense_allocations.reverse", label: "Reverter aplicação" },
      { id: "expense_allocations.export", label: "Exportar" },
      { id: "expense_allocations.rules.manage", label: "Gerenciar regras" },
      { id: "expense_allocations.settings.manage", label: "Gerenciar configuracao" },
      { id: "expense_allocations.audit", label: "Auditar historico" },
    ],
  },
  {
    key: "expenses",
    label: "Despesas",
    actions: [
      { id: "expenses.read", label: "Visualizar" },
      { id: "expenses.create", label: "Criar" },
      { id: "expenses.update_draft", label: "Editar rascunho" },
      { id: "expenses.cancel", label: "Cancelar" },
      { id: "expenses.pay", label: "Pagar" },
      { id: "expenses.refund", label: "Reverter pagamento" },
      { id: "expenses.recurring.manage", label: "Recorrencias" },
      { id: "expenses.categories.manage", label: "Categorias" },
      { id: "expenses.audit", label: "Auditoria" },
      { id: "expenses.export", label: "Exportar" },
      { id: "cash_result.read", label: "Resultado de caixa" },
    ],
  },
  {
    key: "finance",
    label: "Financeiro",
    actions: [
      { id: "finance.read", label: "Visualizar" },
      { id: "finance.write", label: "Criar / Editar" },
      { id: "finance.sales.read", label: "Visualizar vendas" },
      { id: "finance.sales.create", label: "Criar vendas" },
      { id: "finance.sales.cancel", label: "Cancelar vendas" },
      { id: "finance.discounts.apply", label: "Aplicar desconto" },
      { id: "finance.surcharges.apply", label: "Aplicar acrescimo" },
      { id: "finance.receivables.create", label: "Criar recebiveis" },
      { id: "finance.payments.create", label: "Registrar pagamentos" },
      { id: "finance.payments.refund", label: "Estornar pagamentos" },
      { id: "finance.manual_charge.create", label: "Criar cobrancas manuais" },
      { id: "finance.manual_cash.create", label: "Criar entradas manuais" },
      { id: "finance.reconciliation.read", label: "Ver reconciliacao" },
      { id: "finance.reconciliation.resolve", label: "Resolver reconciliacao" },
    ],
  },
  {
    key: "professionals",
    label: "Profissionais",
    actions: [
      { id: "professionals.read", label: "Visualizar" },
      { id: "professionals.write", label: "Criar / Editar" },
    ],
  },
  { key: "users", label: "Usuários", actions: [{ id: "users.read", label: "Visualizar" }] },
];

const APP_PERMISSION_SET = new Set<string>(APP_PERMISSIONS);

export function normalizeUserRole(role?: string | null) {
  return role?.toUpperCase() ?? "";
}

export function isClinicAdmin(user: Pick<AppUser, "role"> | null | undefined): boolean {
  return normalizeUserRole(user?.role) === "ADMIN";
}

export function normalizeUserPermissions(user?: Pick<AppUser, "permissions"> | null): AppPermission[] {
  if (!Array.isArray(user?.permissions)) {
    return [];
  }

  return Array.from(
    new Set(
      user.permissions.filter((permission): permission is AppPermission => APP_PERMISSION_SET.has(permission)),
    ),
  );
}

export function hasPermission(user: AppUser | null | undefined, permission: AppPermission): boolean {
  const role = normalizeUserRole(user?.role);

  if (permission === "medical_records.edit_others" || permission === "medical_records.correct_finalized") {
    return (role === "ADMIN" || role === "PROFESSIONAL")
      && normalizeUserPermissions(user).includes(permission);
  }

  if (isClinicAdmin(user)) {
    return true;
  }

  if (role !== "PROFESSIONAL") {
    return false;
  }

  return normalizeUserPermissions(user).includes(permission);
}

export function hasAnyPermission(
  user: AppUser | null | undefined,
  permissions: AppPermission[] | undefined,
): boolean {
  if (!permissions || permissions.length === 0) {
    return true;
  }

  return permissions.some((permission) => hasPermission(user, permission));
}

export function getFinancePermissions(user: AppUser | null | undefined) {
  return {
    canViewFinance: hasPermission(user, "finance.read"),
    canCreateSale: hasPermission(user, "finance.sales.create"),
    canCreateReceivable: hasPermission(user, "finance.receivables.create"),
    canCreateManualCashEntry: hasPermission(user, "finance.manual_cash.create"),
    canRegisterPayment: hasPermission(user, "finance.payments.create"),
    canRefundPayment: hasPermission(user, "finance.payments.refund"),
    canCancelSale: hasPermission(user, "finance.sales.cancel"),
    canReviewReconciliation: hasPermission(user, "finance.reconciliation.read"),
    canResolveReconciliation: hasPermission(user, "finance.reconciliation.resolve"),
  };
}

export function getClinicalProductionPermissions(user: AppUser | null | undefined) {
  return {
    canReadProduction: hasPermission(user, "clinical_production.read"),
    canGenerateProduction: hasPermission(user, "clinical_production.generate"),
    canCancelProduction: hasPermission(user, "clinical_production.cancel"),
    canAdjustProduction: hasPermission(user, "clinical_production.adjust"),
    canAuditProduction: hasPermission(user, "clinical_production.audit"),
    canExportProduction: hasPermission(user, "clinical_production.export"),
  };
}

export function getPayoutPermissions(user: AppUser | null | undefined) {
  return {
    canReadPayouts: hasPermission(user, "payouts.read"),
    canManagePayoutRules: hasPermission(user, "payouts.rules.manage"),
    canApprovePayouts: hasPermission(user, "payouts.approve"),
    canPayPayouts: hasPermission(user, "payouts.pay"),
    canCancelPayouts: hasPermission(user, "payouts.cancel"),
    canAdjustPayouts: hasPermission(user, "payouts.adjust"),
    canAuditPayouts: hasPermission(user, "payouts.audit"),
    canExportPayouts: hasPermission(user, "payouts.export"),
  };
}

export function getManagementOverviewPermissions(user: AppUser | null | undefined) {
  return {
    canReadOverview: hasPermission(user, "management_overview.read"),
    canExportOverview: hasPermission(user, "management_overview.export"),
    canReadFinancialOverview: hasPermission(user, "management_overview.financial"),
    canReadProductionOverview: hasPermission(user, "management_overview.production"),
    canReadPayoutOverview: hasPermission(user, "management_overview.payouts"),
    canReadExpenseAllocations: hasPermission(user, "expense_allocations.read"),
    canAuditExpenseAllocations: hasPermission(user, "expense_allocations.audit"),
    canExportExpenseAllocations: hasPermission(user, "expense_allocations.export"),
    canReadIncomeStatement: hasPermission(user, "managerial_income_statement.read"),
  };
}

export function getManagerialAccrualPermissions(user: AppUser | null | undefined) {
  return {
    canReadManagerialAccrual: hasPermission(user, "managerial_accrual.read"),
    canReadManagerialAccrualQuality: hasPermission(user, "managerial_accrual.data_quality"),
    canManageManagerialCompetence: hasPermission(user, "managerial_accrual.manage_competence"),
    canBackfillManagerialCompetence: hasPermission(user, "managerial_accrual.backfill"),
  };
}

export function getManagerialIncomeStatementPermissions(user: AppUser | null | undefined) {
  return {
    canReadIncomeStatement: hasPermission(user, "managerial_income_statement.read"),
    canDrilldownIncomeStatement: hasPermission(user, "managerial_income_statement.drilldown"),
    canExportIncomeStatement: hasPermission(user, "managerial_income_statement.export"),
    canReadIncomeStatementQuality: hasPermission(user, "managerial_income_statement.data_quality"),
    canReadExpenseAllocations: hasPermission(user, "expense_allocations.read"),
    canAuditExpenseAllocations: hasPermission(user, "expense_allocations.audit"),
    canExportExpenseAllocations: hasPermission(user, "expense_allocations.export"),
  };
}

export function getExpenseAllocationPermissions(user: AppUser | null | undefined) {
  return {
    canReadExpenseAllocations: hasPermission(user, "expense_allocations.read"),
    canSimulateExpenseAllocations: hasPermission(user, "expense_allocations.simulate"),
    canApplyExpenseAllocations: hasPermission(user, "expense_allocations.apply"),
    canReverseExpenseAllocations: hasPermission(user, "expense_allocations.reverse"),
    canExportExpenseAllocations: hasPermission(user, "expense_allocations.export"),
    canManageExpenseAllocationRules: hasPermission(user, "expense_allocations.rules.manage"),
    canManageExpenseAllocationSettings: hasPermission(user, "expense_allocations.settings.manage"),
    canAuditExpenseAllocations: hasPermission(user, "expense_allocations.audit"),
  };
}

export function getExpensePermissions(user: AppUser | null | undefined) {
  return {
    canReadExpenses: hasPermission(user, "expenses.read"),
    canCreateExpenses: hasPermission(user, "expenses.create"),
    canUpdateDraftExpenses: hasPermission(user, "expenses.update_draft"),
    canCancelExpenses: hasPermission(user, "expenses.cancel"),
    canPayExpenses: hasPermission(user, "expenses.pay"),
    canRefundExpenses: hasPermission(user, "expenses.refund"),
    canManageRecurringExpenses: hasPermission(user, "expenses.recurring.manage"),
    canManageExpenseCategories: hasPermission(user, "expenses.categories.manage"),
    canAuditExpenses: hasPermission(user, "expenses.audit"),
    canExportExpenses: hasPermission(user, "expenses.export"),
    canReadCashResult: hasPermission(user, "cash_result.read"),
  };
}
